import { createAction, props } from '@ngrx/store';

import { IGraphQLErrors } from '@shared/models/graphql';
import {
  ISignInInput,
  ISignInWithMagicLinkInput,
  ISignInWithEmailMagicLinkInput,
  ISignInWithSmsMagicLinkInput,
  ISignUpInput,
  IPasswordResetWithPasswordInput,
  IConfirmNumberInput,
  AuthModal,
  ISignUp,
  IRegisterPasswordlessInput,
} from '@shared/models/auth';
import {
  IUser,
  IUserWithToken,
  IUserPreference,
  IUserPreferenceInput,
} from '@shared/models/user';
import {
  IUpdateProfileData,
  IUpdateProfileInput,
  IUpdatePasswordInput,
  IUpdatePhoneNumberInput,
} from '@shared/models/profile';
import { IOrganization } from '@shared/models/organization';

export const setLoadingAction = createAction(
  '[Auth] Set Loading',
  props<{ loading: boolean }>()
);

export const signInAction = createAction(
  '[Auth] Sign In',
  props<ISignInInput>()
);

export const signInActionSuccess = createAction(
  '[Auth] Sign In Success',
  props<IUserWithToken>()
);

export const signInActionFailure = createAction(
  '[Auth] Sign In Failure',
  props<IGraphQLErrors>()
);

export const signInWithMagicLinkAction = createAction(
  '[Auth] Sign In With Magic Link',
  props<{ input: ISignInWithMagicLinkInput; url: string; errType?: 'embed' }>()
);

export const signInWithMagicLinkActionFailure = createAction(
  '[Auth] Sign In With Magic Link Failure',
  props<{ showError?: boolean; errType?: 'embed' }>()
);

export const signInWithEmailMagicLinkAction = createAction(
  '[Auth] Sign In With Email Magic Link',
  props<{ input: ISignInWithEmailMagicLinkInput; url: string }>()
);

export const signInWithEmailMagicLinkActionFailure = createAction(
  '[Auth] Sign In With Email Magic Link Failure',
  props<IGraphQLErrors>()
);

export const signInWithSmsMagicLinkAction = createAction(
  '[Auth] Sign In With SMS Magic Link',
  props<{ input: ISignInWithSmsMagicLinkInput; url: string }>()
);

export const signInWithSmsMagicLinkActionFailure = createAction(
  '[Auth] Sign In With SMS Magic Link Failure',
  props<IGraphQLErrors>()
);

export const signUpAction = createAction(
  '[Auth] Sign Up',
  props<ISignUpInput>()
);

export const signUpActionSuccess = createAction(
  '[Auth] Sign Up Success',
  props<ISignUp>()
);

export const signUpActionFailure = createAction(
  '[Auth] Sign Up Failure',
  props<IGraphQLErrors>()
);

export const registerPasswordlessAction = createAction(
  '[Auth] Register Passwordless',
  props<IRegisterPasswordlessInput>()
);

export const registerPasswordlessActionFailure = createAction(
  '[Auth] Register Passwordless Failure',
  props<IGraphQLErrors>()
);

export const updateAuthOrganizationAction = createAction(
  '[Auth] Update Auth Organization',
  props<{ org: IOrganization }>()
);

export const signOutAction = createAction(
  '[Auth] Sign Out',
  props<{ skipRedirect?: boolean }>()
);

export const setupAccountAction = createAction(
  '[Auth] Setup Account',
  props<IUserPreferenceInput>()
);

export const setupAccountActionSuccess = createAction(
  '[Auth] Setup Account Success',
  props<{
    preference: IUserPreference;
    cbsaPreferences?: IUserPreferenceInput['input']['cbsaPreferences'];
    entities?: IUserPreferenceInput['input']['entities'];
  }>()
);

export const setupAccountActionFailure = createAction(
  '[Auth] Setup Account Failure',
  props<IGraphQLErrors>()
);

export const updateUserPreferenceAction = createAction(
  '[Auth] Update User Preference',
  props<{ id: string; input: IUserPreferenceInput['input'] }>()
);

export const updateUserPreferenceActionSuccess = createAction(
  '[Auth] Update User Preference Success',
  props<{
    preference: IUserPreference;
    cbsaPreferences?: IUserPreferenceInput['input']['cbsaPreferences'];
  }>()
);

export const updateUserPreferenceActionFailure = createAction(
  '[Auth] Update User Preference Failure',
  props<IGraphQLErrors>()
);

export const fetchUserAction = createAction('[Auth] Fetch User');

export const fetchUserActionSuccess = createAction(
  '[Auth] Fetch User Success',
  props<IUser>()
);

export const fetchUserActionFailure = createAction(
  '[Auth] Fetch User Failure',
  props<IGraphQLErrors>()
);

export const updateUserConfirmedAtStatuses = createAction(
  '[Auth] Update User Confirmed At Statuses',
  props<{
    emailConfirmedAt: string | null;
    phoneNumberConfirmedAt: string | null;
  }>()
);

export const clearRequestPasswordResetAction = createAction(
  '[Auth] Clear Request Password Reset'
);

export const requestPasswordResetAction = createAction(
  '[Auth] Request Password Reset',
  props<{ email: string }>()
);

export const requestPasswordResetActionFinish = createAction(
  '[Auth] Request Password Reset Finish',
  props<{ success: boolean }>()
);

export const resetPasswordAction = createAction(
  '[Auth] Reset Password',
  props<IPasswordResetWithPasswordInput>()
);

export const resetPasswordActionFinish = createAction(
  '[Auth] Reset Password Finish'
);

export const sendPhoneNumberConfirmationAction = createAction(
  '[Auth] Send Phone Number Confirmation'
);

export const confirmPhoneNumberAction = createAction(
  '[Auth] Confirm Phone Number',
  props<IConfirmNumberInput>()
);

export const updatePhoneNumberAction = createAction(
  '[Auth] Update Phone Number',
  props<IUpdatePhoneNumberInput>()
);

export const updatePhoneNumberActionFailure = createAction(
  '[Auth] Update Phone Number Failure',
  props<IGraphQLErrors>()
);

export const updatePhoneNumberSuccessAction = createAction(
  '[Auth] Update Phone Number Success',
  props<{ success: boolean; user: IUser }>()
);

export const setReferrerCodeAction = createAction(
  '[Auth] Set Referrer Code',
  props<{ referrerCode: string | null }>()
);

export const setEntityIdAction = createAction(
  '[Auth] Set Entity Id',
  props<{ entityId: string | null }>()
);

export const setLastSMSSentAction = createAction(
  '[Auth] Set SMS Last Sent',
  props<{ lastSMSSent?: string }>()
);

export const updateProfileAction = createAction(
  '[Auth] Update Profile',
  props<IUpdateProfileInput>()
);

export const updateProfileActionSuccess = createAction(
  '[Auth] Update Profile Success',
  props<IUpdateProfileData>()
);

export const updateProfileActionFailure = createAction(
  '[Auth] Update Profile Failure',
  props<IGraphQLErrors>()
);

export const updatePasswordAction = createAction(
  '[Auth] Update Password',
  props<IUpdatePasswordInput>()
);

export const updatePasswordActionSuccess = createAction(
  '[Auth] Update Password Success'
);

export const updatePasswordActionFailure = createAction(
  '[Auth] Update Password Failure',
  props<IGraphQLErrors>()
);

export const setAuthModal = createAction(
  '[Auth] Set Auth Modal',
  props<{
    authModal?: AuthModal;
    defaultEmail?: string;
    defaultPhoneNumber?: string;
  }>()
);

export const setAuthDefaultValues = createAction(
  '[Auth] Set Auth Default Values',
  props<{
    email?: string;
    phoneNumber?: string;
  }>()
);
